/*
 * Buy-the-pieces block (snippets/naswan-pieces.liquid). Each row is one piece
 * of a 3-piece article - shalwar kameez (sized) and dupatta (one size) - with
 * its own quantity. "Add to cart" adds every row whose quantity is above zero
 * in a single /cart/add.js call, then hands the response to the cart drawer
 * the same way Dawn's product form does.
 */
if (!customElements.get('naswan-pieces')) {
  customElements.define(
    'naswan-pieces',
    class NaswanPieces extends HTMLElement {
      connectedCallback() {
        this.rows = Array.from(this.querySelectorAll('[data-piece]'));
        this.totalEl = this.querySelector('[data-total]');
        this.errorEl = this.querySelector('[data-error]');
        this.addBtn = this.querySelector('[data-add]');
        this.format = this.dataset.moneyFormat || '{{amount}}';

        this.rows.forEach((row) => {
          row.querySelectorAll('[data-qty-step]').forEach((btn) =>
            btn.addEventListener('click', () => this.step(row, parseInt(btn.dataset.qtyStep, 10)))
          );
          row.querySelector('[data-qty]')?.addEventListener('input', () => this.update());
          row.querySelectorAll('.naswan-size[data-variant-id]').forEach((btn) =>
            btn.addEventListener('click', () => this.selectSize(row, btn))
          );
        });

        this.querySelectorAll('[data-size-guide]').forEach((btn) =>
          btn.addEventListener('click', () => document.querySelector('.naswan-size-chart__opener')?.click())
        );
        this.addBtn.addEventListener('click', () => this.add());
        this.update();
      }

      qty(row) {
        const input = row.querySelector('[data-qty]');
        if (!input || input.disabled) return 0;
        return Math.max(0, Math.min(20, parseInt(input.value, 10) || 0));
      }

      step(row, delta) {
        const input = row.querySelector('[data-qty]');
        if (!input || input.disabled) return;
        input.value = Math.max(0, Math.min(20, (parseInt(input.value, 10) || 0) + delta));
        this.update();
      }

      selectSize(row, btn) {
        row.querySelectorAll('.naswan-size[data-variant-id]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        row.dataset.variantId = btn.dataset.variantId;
        row.dataset.price = btn.dataset.price;
        const sku = row.querySelector('[data-sku]');
        if (sku) sku.textContent = btn.dataset.sku || '—';
        const price = row.querySelector('[data-price]');
        if (price) price.textContent = this.money(btn.dataset.price);
        this.hideError();
        this.update();
      }

      // Unselected sized rows still count towards the total at their first
      // size's price, so the shopper sees the set price before choosing.
      rowPrice(row) {
        if (row.dataset.price) return parseInt(row.dataset.price, 10);
        const first = row.querySelector('.naswan-size[data-variant-id]');
        return first ? parseInt(first.dataset.price, 10) : 0;
      }

      update() {
        const total = this.rows.reduce((sum, row) => sum + this.rowPrice(row) * this.qty(row), 0);
        this.totalEl.textContent = this.money(total);
        this.addBtn.disabled = total === 0;
      }

      money(cents) {
        const value = (parseInt(cents, 10) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return this.format.replace(/\{\{\s*\w+\s*\}\}/, value);
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        this.errorEl.hidden = true;
      }

      async add() {
        this.hideError();
        const items = [];
        for (const row of this.rows) {
          const quantity = this.qty(row);
          if (!quantity) continue;
          if (!row.dataset.variantId) {
            this.showError(`Please choose a size for the ${row.dataset.label}.`);
            row.querySelector('.naswan-piece__sizes')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          items.push({ id: parseInt(row.dataset.variantId, 10), quantity });
        }
        if (!items.length) {
          this.showError('Choose at least one piece.');
          return;
        }

        const drawer = document.querySelector('cart-drawer');
        const body = { items };
        if (drawer) {
          body.sections = drawer.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }

        this.addBtn.setAttribute('aria-busy', 'true');
        this.addBtn.disabled = true;
        try {
          const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (!res.ok || data.status) {
            this.showError(data.description || data.message || 'Could not add to cart. Please try again.');
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'naswan-pieces', cartData: data });
          }
          if (drawer) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } else {
            window.location = window.routes?.cart_url || '/cart';
          }
        } catch (e) {
          this.showError('Could not add to cart. Please check your connection and try again.');
        } finally {
          this.addBtn.removeAttribute('aria-busy');
          this.update();
        }
      }
    }
  );
}
